import React from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
	AudioMutedOutlined,
	AudioOutlined,
	CloseOutlined,
	CommentOutlined,
	DeleteOutlined,
	ExpandAltOutlined,
	HistoryOutlined,
	MinusOutlined,
	MoreOutlined,
	PlusOutlined,
} from '@ant-design/icons';

import MemoryProposalsChip from './MemoryProposalsChip';
import XoeMark from './XoeMark';
import XoePatientChip from './XoePatientChip';
import { useXoeSize } from './XoeSurface';
import type { XoeChatHeaderProps } from '../../types/xoe/XoeUiTypes';

const XoeChatHeader: React.FC<XoeChatHeaderProps> = ({
	tone,
	title,
	boundPatientName,
	pendingMemoryProposals,
	onReviewMemory,
	rail,
	onNewConversation,
	onNewGeneralConversation,
	showNewGeneral,
	onDelete,
	canDelete,
	mic,
	onMaximise,
	maximiseDisabled = false,
	onClose,
	closeVariant = 'close',
	notice,
	extra,
}) => {
	const compact = useXoeSize() === 'compact';

	const railLabel = rail.collapsed ? 'Show conversations' : 'Hide conversations';
	const micLabel = !mic?.supported ? 'Voice input not supported in this browser' : mic.active ? 'Stop voice input' : 'Voice input';
	const maxTooltip = maximiseDisabled ? 'Available when Xoe finishes' : 'Maximise';
	const closeLabel = closeVariant === 'minimise' ? 'Minimize Xoe chat' : 'Close Xoe';

	// Compact containers fold the secondary actions into one menu; the same test ids ride the items.
	const menuItems: MenuProps['items'] = [
		...(showNewGeneral
			? [{ key: 'new-general', label: <span data-testid="xoe-new-general-conversation">New general conversation</span> }]
			: []),
		...(mic
			? [{ key: 'mic', disabled: !mic.supported, label: <span data-testid="xoe-toggle-mic">{micLabel}</span> }]
			: []),
		{
			key: 'delete',
			danger: true,
			disabled: !canDelete,
			label: <span data-testid="xoe-delete-conversation">Delete conversation</span>,
		},
		...(onMaximise
			? [{ key: 'max', disabled: maximiseDisabled, label: <span data-testid="xoe-toggle-max">Maximise</span> }]
			: []),
	];

	const onMenuClick: NonNullable<MenuProps['onClick']> = ({ key }) => {
		if (key === 'new-general') onNewGeneralConversation();
		if (key === 'mic') mic?.onToggle();
		if (key === 'delete') onDelete();
		if (key === 'max') onMaximise?.();
	};

	const showSubRow = pendingMemoryProposals > 0 || notice != null;

	return (
		<div className={`mg-xoe-header mg-xoe-header--${tone}`} data-testid="xoe-chat-header" data-tone={tone}>
			<div className="mg-xoe-header-row">
				<span className="mg-xoe-header-mark">
					<XoeMark size={16} showMonogram={false} animated={false} />
				</span>
				<div className="mg-xoe-header-title" data-testid="xoe-conversation-title">
					{title}
				</div>
				{boundPatientName && <XoePatientChip name={boundPatientName} data-testid="xoe-bound-patient" />}
				{extra}

				<div className="mg-xoe-header-actions">
					<Tooltip title={railLabel}>
						<Button
							type="text"
							size="small"
							icon={<HistoryOutlined />}
							className={rail.collapsed ? undefined : 'is-active'}
							onClick={rail.onToggle}
							aria-label={railLabel}
							aria-pressed={!rail.collapsed}
							data-testid="xoe-toggle-list"
						/>
					</Tooltip>
					<Tooltip title="New conversation">
						<Button
							type="text"
							size="small"
							icon={<PlusOutlined />}
							onClick={onNewConversation}
							aria-label="New conversation"
							data-testid="xoe-new-conversation"
						/>
					</Tooltip>

					{compact ? (
						<Dropdown trigger={['click']} placement="bottomRight" menu={{ items: menuItems, onClick: onMenuClick }}>
							<Button
								type="text"
								size="small"
								icon={<MoreOutlined />}
								aria-label="More conversation actions"
								data-testid="xoe-header-more"
							/>
						</Dropdown>
					) : (
						<>
							{showNewGeneral && (
								<Tooltip title="New general conversation">
									<Button
										type="text"
										size="small"
										icon={<CommentOutlined />}
										onClick={onNewGeneralConversation}
										aria-label="New general conversation"
										data-testid="xoe-new-general-conversation"
									/>
								</Tooltip>
							)}
							{mic && (
								<Tooltip title={micLabel}>
									<Button
										type="text"
										size="small"
										icon={mic.active ? <AudioMutedOutlined /> : <AudioOutlined />}
										className={mic.active ? 'is-active' : undefined}
										disabled={!mic.supported}
										onClick={mic.onToggle}
										aria-label="Toggle voice input"
										aria-pressed={mic.active}
										data-testid="xoe-toggle-mic"
									/>
								</Tooltip>
							)}
							<Tooltip title="Delete conversation">
								<Button
									type="text"
									size="small"
									icon={<DeleteOutlined />}
									disabled={!canDelete}
									onClick={onDelete}
									aria-label="Delete conversation"
									data-testid="xoe-delete-conversation"
								/>
							</Tooltip>
							{onMaximise && (
								<Tooltip title={maxTooltip}>
									{/* A disabled button fires no hover, so the span carries the tooltip. */}
									<span className="mg-xoe-tooltip-target">
										<Button
											type="text"
											size="small"
											icon={<ExpandAltOutlined />}
											disabled={maximiseDisabled}
											onClick={onMaximise}
											aria-label="Maximise"
											data-testid="xoe-toggle-max"
										/>
									</span>
								</Tooltip>
							)}
						</>
					)}

					{onClose && (
						<Tooltip title={closeVariant === 'minimise' ? 'Minimize' : 'Close'}>
							<Button
								type="text"
								size="small"
								icon={closeVariant === 'minimise' ? <MinusOutlined /> : <CloseOutlined />}
								onClick={onClose}
								aria-label={closeLabel}
								data-testid="xoe-close"
							/>
						</Tooltip>
					)}
				</div>
			</div>

			{/* Its own row: the title row is full at 440px and the chip must stay readable. */}
			{showSubRow && (
				<div className="mg-xoe-header-row mg-xoe-header-row--sub">
					<MemoryProposalsChip
						count={pendingMemoryProposals}
						onReview={onReviewMemory}
						tone={tone === 'primary' ? 'on-primary' : 'neutral'}
					/>
					{notice != null && (
						<span className="mg-xoe-header-notice" data-testid="xoe-header-notice">
							{notice}
						</span>
					)}
				</div>
			)}
		</div>
	);
};

export default XoeChatHeader;
